<template>
  <div :class="['widget-body', viewMode]" v-resize:throttle.100="setWidgetViewMode">
    <v-alert show v-if="isNoData" variant="light" align="center">{{ $t('white-label.globalMessages.nodata') }}</v-alert>
    <div v-else>
      <div class="header">{{ i18n('sections.analyst-summary') }}</div>
      <div class="summary-container">
        <div v-for="(tile, i) in summaryData" :key="i" class="tile">
          <div class="label">{{ i18n('labels.' + tile.label) }}</div>
          <div class="value" :class="tile.cssClass">{{ tile.value }}</div>
        </div>
      </div>

      <div class="header">{{ i18n('sections.analyst-assessment') }}</div>
      <div class="assessment-container">
        <div v-for="(entity, i) in assessmentData" :key="i">
          <c-analyst-assessment-bar-chart :data="entity" />
        </div>
        <div>
          <c-analyst-assessment-bar-chart legend />
        </div>
      </div>

      <div class="header">{{ i18n('sections.average-values') }}</div>
      <table
        class="table table-dark table-striped average-values-container"
        :aria-label="i18n('sections.average-values')"
      >
        <thead>
          <tr>
            <th class="text-left">{{ i18n('labels.period') }}</th>
            <th class="text-center">{{ i18n('labels.analyst-per-lvl') }}</th>
            <th class="text-right">{{ i18n('labels.mean') }}</th>
            <th class="text-right">{{ i18n('labels.total-analysts') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(entity, i) in averageValuesData" :key="i">
            <td>{{ i18n('labels.' + entity.label) }}</td>
            <td>
              <c-analysts-chart
                :buy="entity.buy"
                :outperform="entity.outperform"
                :hold="entity.hold"
                :underperform="entity.underperform"
                :sell="entity.sell"
                :mean="entity.mean"
              />
            </td>
            <td class="text-right">
              {{ isNaN(entity.mean) ? i18n('n/a', 'globalLabels') : $f(entity.mean, 'float') }}
            </td>
            <td class="text-right">{{ isNaN(entity.total) ? i18n('n/a', 'globalLabels') : entity.total }}</td>
          </tr>
          <tr aria-hidden="true" role="presentation">
            <td></td>
            <td>
              <div class="legend">
                <div v-for="(label, i) in ['strong-buy', 'buy', 'hold', 'sell', 'strong-sell']" :key="label">
                  <div class="label">{{ i18n('labels.' + label) }}</div>
                  <div :class="label">{{ i + 1 }}</div>
                </div>
              </div>
            </td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </table>
      <div class="disclaimer p-1 mt-3 font-italic">
        {{ i18n('disclaimer') }}
      </div>
    </div>
  </div>
</template>

<script>
import CAnalystAssessmentBarChart from '@custom/CAnalystAssessmentBarChart';
import CAnalystsChart from '@custom/CAnalystsChart';
import resize from 'vue-resize-directive';
import widgetCommons from '@mixins/widgetCommons';
import { API } from '@state/api';

export default {
  name: 'w-analyst-evaluation-widget-body',
  components: {
    CAnalystAssessmentBarChart,
    CAnalystsChart
  },
  directives: {
    resize
  },
  mixins: [widgetCommons],
  props: {
    ric: {
      type: String,
      required: true
    },
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isNoData: false,
      evaluationsData: {},
      fids: [
        'q._TRDPRC_1',
        'q.RIC',
        'rkd.RECOM_MEAN_CURR',
        'rkd.RECOM_MEAN_1WA',
        'rkd.RECOM_MEAN_1MA',
        'rkd.RECOM_MEAN_2MA',
        'rkd.RECOM_NUM_BUY_CURR',
        'rkd.RECOM_NUM_BUY_1WA',
        'rkd.RECOM_NUM_BUY_1MA',
        'rkd.RECOM_NUM_BUY_2MA',
        'rkd.RECOM_NUM_OUTPERFORM_CURR',
        'rkd.RECOM_NUM_OUTPERFORM_1WA',
        'rkd.RECOM_NUM_OUTPERFORM_1MA',
        'rkd.RECOM_NUM_OUTPERFORM_2MA',
        'rkd.RECOM_NUM_HOLD_CURR',
        'rkd.RECOM_NUM_HOLD_1WA',
        'rkd.RECOM_NUM_HOLD_1MA',
        'rkd.RECOM_NUM_HOLD_2MA',
        'rkd.RECOM_NUM_UNDERPERFORM_CURR',
        'rkd.RECOM_NUM_UNDERPERFORM_1WA',
        'rkd.RECOM_NUM_UNDERPERFORM_1MA',
        'rkd.RECOM_NUM_UNDERPERFORM_2MA',
        'rkd.RECOM_NUM_SELL_CURR',
        'rkd.RECOM_NUM_SELL_1WA',
        'rkd.RECOM_NUM_SELL_1MA',
        'rkd.RECOM_NUM_SELL_2MA',
        'rkd4.TARGETPRICE'
      ],
      periodMap: {
        CURR: 'current',
        '1WA': '1W-ago',
        '1MA': '1M-ago',
        '2MA': '2M-ago'
      },
      quoteData: {},
      viewBreakpoints: {
        780: 'm',
        500: 's'
      },
      viewMode: ''
    };
  },
  watch: {
    ric() {
      this.fetch();
    }
  },
  computed: {
    assessmentData() {
      if (!this.evaluationsData || !Object.keys(this.evaluationsData).length) return [];
      return Array.from(Array(5), (_, i) => ({
        FINQ: this.evaluationsData['FINQ_NXT' + (i + 1)] || '-',
        AE_EPS: this.parseAEEPS(this.evaluationsData[`AE_EPS_3MA_NXT${i + 1}Q`])
      }));
    },
    averageValuesData() {
      if (!this.quoteData || !Object.keys(this.quoteData).length) return [];
      return ['CURR', '1WA', '1MA', '2MA'].map(spec => ({
        label: this.periodMap[spec],
        buy: +this.quoteData['rkd.RECOM_NUM_BUY_' + spec],
        outperform: +this.quoteData['rkd.RECOM_NUM_OUTPERFORM_' + spec],
        hold: +this.quoteData['rkd.RECOM_NUM_HOLD_' + spec],
        underperform: +this.quoteData['rkd.RECOM_NUM_UNDERPERFORM_' + spec],
        sell: +this.quoteData['rkd.RECOM_NUM_SELL_' + spec],
        mean: +this.quoteData['rkd.RECOM_MEAN_' + spec],
        total: ['BUY', 'OUTPERFORM', 'HOLD', 'UNDERPERFORM', 'SELL'].reduce(
          (acc, i) => acc + Number(this.quoteData[`rkd.RECOM_NUM_${i}_${spec}`]),
          0
        )
      }));
    },
    summaryData() {
      if (!this.quoteData) {
        return [];
      }
      if (!Object.keys(this.quoteData).length) return [];
      const recommendation = {
        cssClass: '',
        label: 'recommendation',
        value: this.$f(this.quoteData['rkd.RECOM_MEAN_CURR'], 'float')
      };
      const average = {
        label: 'analyst-average'
      };
      const targetPrice = {
        cssClass: '',
        label: 'target-price',
        value: this.$f(this.quoteData['rkd4.TARGETPRICE'], 'float.price')
      };
      const diffToPrice = {
        cssClass: '',
        label: 'diff-to-price',
        value: '-'
      };
      let cta = 'strong-sell';
      const recommendationValue = Number.parseFloat(this.quoteData['rkd.RECOM_MEAN_CURR']);
      if (!isNaN(recommendationValue)) {
        if (recommendationValue < 1.5) cta = 'strong-buy';
        else if (recommendationValue < 2.5) cta = 'buy';
        else if (recommendationValue < 3.5) cta = 'hold';
        else if (recommendationValue < 4.5) cta = 'sell';
      }
      average.value = this.i18n('labels.' + cta);
      average.cssClass = recommendation.cssClass = cta;

      if (!isNaN(this.quoteData['rkd4.TARGETPRICE']) && !isNaN(this.quoteData['q._TRDPRC_1'])) {
        diffToPrice.value = this.$f(
          (this.quoteData['rkd4.TARGETPRICE'] / this.quoteData['q._TRDPRC_1'] - 1) * 100,
          'float.percentage.change'
        );
        diffToPrice.cssClass = targetPrice.cssClass =
          this.quoteData['q._TRDPRC_1'] < targetPrice.value ? 'positive' : 'negative';
      }
      return [recommendation, average, targetPrice, diffToPrice];
    }
  },
  methods: {
    fetch() {
      this.isNoData = false;
      API.Rest.get(`/quote/info?rics=${this.ric}&fids=${this.fids}`)
        .catch(err => {
          this.isNoData = true;
          console.error(err);
        })
        .then(response => {
          this.quoteData = response.data.data[0];
        });
      API.Rest.get(`/company/analystevaluations?rics=${this.ric}`)
        .catch(err => {
          console.error(err);
        })
        .then(response => {
          this.evaluationsData = response.data.data[0];
        });
    },
    parseAEEPS(eps) {
      if (!eps || !/\d+\/\d+\/\d+/.test(eps)) return { unchanged: '-', raised: '-', lowered: '-' };
      const [all, raised, lowered] = eps.split('/');
      return {
        unchanged: all - raised - lowered,
        raised,
        lowered
      };
    },
    setWidgetViewMode() {
      const wBodyWidth = this.$el.getBoundingClientRect().width;
      for (const width in this.viewBreakpoints) {
        if (wBodyWidth <= width) {
          this.viewMode = this.viewBreakpoints[width];
          return;
        }
      }
      this.viewMode = 'l';
    }
  },
  mounted() {
    this.fetch();
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/analyst-evaluation-widget';
</style>
